"use client";

import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { LOCALES, DEFAULT_LOCALE, LOCALE_COOKIE } from "@kkeut/lib/locale";

/**
 * 전 지점 공용 로케일 Context.
 * provider(각 앱의 LocaleProvider)와 consumer(packages/ui 공유 컴포넌트)가
 * 반드시 "이 동일한 Context 인스턴스"를 공유해야 useLocale() 값이 맞물린다.
 */
export const LocaleContext = createContext({ locale: DEFAULT_LOCALE, setLocale: () => {} });

export const useLocale = () => useContext(LocaleContext);

/**
 * 전 지점 공용 LocaleProvider.
 * 각 앱 `@/components/LocaleProvider` 의 캐노니컬(25개 동일) 버전과 동작이 동일하며,
 * 공용 Context 와 같은 모듈에 co-locate 하여 컨텍스트 인스턴스 동일성을 보장한다.
 * (etc-layout 등 공유 shell 이 앱의 `@/` 경로 없이 provider 를 렌더링할 수 있도록.)
 */
export function LocaleProvider({ initialLocale = DEFAULT_LOCALE, children }) {
  const [locale, setLocaleState] = useState(initialLocale);
  const pathname = usePathname();

  /** URL 접두어(/en|/ja|/zh)가 있으면 그걸 최우선으로 반영 */
  useEffect(() => {
    const m = /^\/(en|ja|zh|th)(?=\/|$)/.exec(pathname || "");
    const next = m ? m[1] : "ko";
    if ([...LOCALES, "th"].includes(next) && next !== locale) {
      setLocaleState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  /** 사용자 전환: 상태 + 쿠키만 갱신 (URL 이동은 스위처에서 처리) */
  const setLocale = (next) => {
    if (![...LOCALES, "th"].includes(next)) return;
    setLocaleState(next);
    document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000; SameSite=Lax`;
  };

  const value = useMemo(() => ({ locale, setLocale }), [locale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
